import { useState, useEffect, useRef } from "react";
import "./SearchableDropdown.css";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";

const SearchableDropdown = ({
  options,
  placeholder,
  onSelect,
  value,
  idForLabel,
  disabled = false,
  mandatory = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // controls rendering
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const { t } = useTranslations();

  const dropdownRef = useRef();

  useEffect(() => {
    setFilteredOptions(
      options.filter((option) =>
        option.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openDropdown = () => {
    setIsVisible(true); // render first
    requestAnimationFrame(() => setIsOpen(true)); // then animate
    setSearchTerm("");
  };

  const closeDropdown = () => {
    setIsOpen(false); // start fade out
  };

  const handleTransitionEnd = () => {
    if (!isOpen) setIsVisible(false); // remove from DOM after fade
  };

  const handleSelect = (option) => {
    onSelect(option.value);
    closeDropdown();
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  return (
    <div
      className={`dropdownContainer ${disabled ? "dropdown-disabled" : ""}`}
      ref={dropdownRef}
      onClick={toggleDropdown}
    >
      <div className={`dropdown ${disabled ? "dropdown-disabled" : ""}`}>
        <button type="button" onClick={toggleDropdown} disabled={disabled}>
          {idForLabel}&nbsp;
          {mandatory ? <span style={{ color: "red" }}>*</span> : ""}
        </button>

        <div
          className={`dropdown-header ${disabled ? "disabled" : ""}`}
          onClick={toggleDropdown}
        >
          {value
            ? options.find((option) => option.value === value)?.label ||
              placeholder
            : placeholder}
        </div>

        {isVisible && (
          <div
            className={`dropdown-body ${isOpen ? "open" : "closed"} ${
              disabled ? "disabled" : ""
            }`}
            style={{
              opacity: isOpen ? 1 : 0,
              transition: "opacity 0.3s ease-out",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            <input
              id={idForLabel}
              type="text"
              placeholder={t.searchableDropdown.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
            />
            <ul className="dropdown-list">
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className="dropdown-item"
                >
                  {option.label}
                </li>
              ))}
              {filteredOptions.length === 0 && (
                <li className="dropdown-item">
                  {t.searchableDropdown.noNameFound}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;
